import React, { useEffect, useState, useCallback } from 'react';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import AddClipModal from '../../components/AddClipModal/AddClipModal';
import AddClipContent from '../../components/AddClipContent/AddClipContent';
import ClipsProvider from '../../context/provider/ClipsProvider';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { timeStampToMs } from '../../utils/TimeUtil';
import VideoService from '../../service/VideoService';
import arrayMove from '../../utils/arrayUtil';
import useEditorActions from '../../hooks/useEditorActions';
import EditorContent from '../../components/EditorContent/EditorContent';
import ResourceHelper from '../../utils/ResourceHelper';
import CmsAPI from '../../../common/apis/CmsAPI';
import { useTrimContext } from '../../context/VideoTrimContex';
import configs from '../../../../conf';
import { t } from 'i18next';
import { useIntroOutroContext } from '../../context/IntroOutroContext';
import { TRANSITION_TYPE } from '../../constants/editorConst';
import { useUserContext } from '../../context/UserContext';
import { uploadScenesTextPanelsPng } from '../../utils/SimulatedItemsUtility';
import { v4 as uuidv4 } from 'uuid';
import { TextPanelProvider } from '../../context/TextPanelContext';
import { ToastNotification } from '../../components/Toast/Toast';
import { withPermissionCheck } from '../../hocs/withPermissionCheck';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import usePrevious from '../../../common/hooks/usePrevious';
import useWaitForJobsToStart from '../../hooks/useWaitForJobsToStart';
import { useVideoSpeedUpContext } from '../../context/VideoSpeedUpContext';
import useIsAssignmentsRead from '../../hooks/useIsAssignmentsRead';
import { useZoomContext } from '../../context/ZoomContext';
import { useLogoContext } from '../../context/LogoContext';
import { STORY_USER_RELATION } from '../../constants/UserRoles';
import { parseString } from '../../utils/stringHelper';
import UploadClipTab from '../../components/UploadClipTab/UploadClipTab';
import UploadClipModal from '../../features/Editor/components/UploadClipModal/UploadClipModal';
import InstallExtensionModal from '../../components/InstallExtensionModal/InstallExtensionModal';
import * as Sentry from '@sentry/browser';
import { useSceneJobs } from 'engage/features/Storyboard/hooks/useSceneJobs';
import { assignFontsForConfig } from 'engage/utils/videoUtils';
import { ScenesType } from '../../../common/apis/CmsAPI';

const ENHANCE_JOB_LABEL = 'enhanced';
const AVATAR_JOB_LABEL = 'avatar';

const EXTERNAL_JOBS_LIST = [ENHANCE_JOB_LABEL, AVATAR_JOB_LABEL];

const config = configs[process.env.APP_CONF];
const EXTENSION_ID = process.env.EXT_ID;

const Editor = ({ videoId, initialVideoDetails, userPermissions, storyRole, initialVideoConfig }) => {
  const [videoByParts, setVideoByParts] = useState({});
  const [currentVideoPart, setCurrentVideoPart] = useState('0');
  const [scenes, setScenes] = useState([]);
  const [sceneIndex, setSceneIndex] = useState(initialVideoDetails.hasIntro ? 1 : 0);
  const [showSelectFromLibraryModal, setShowSelectFromLibraryModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState({ show: false });
  const [clipsPosition, setClipsPosition] = useState({ index: 0, before: false });
  const [video, setVideo] = useState(null);
  const [init, setInit] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newResources, setNewResources] = useState(null);
  const [isClipUploading, setIsClipUploading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [jobsBuffer, setJobsBuffer] = useState([]);
  const [failedJob, setFailedJob] = useState(null);
  const [transitions, setTransitions] = useState([]);
  const [recordedClip, setRecordedClip] = useState(null);
  const previousScenes = usePrevious(scenes);
  const [isStopBackgroundMusic, setIsStopBackgroundMusic] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [logo, setLogo] = useState(null);
  const [videoConfig, setVideoConfig] = useState(initialVideoConfig);

  const [isShowExtensionModal, setIsShowExtensionModal] = useState(false);

  const [newlyRecordingSceneId, setNewlyRecordingSceneId] = useState(null);

  const {
    initialIntroOutro,
    chooseIntroOutro,
    changeInitialIntroOutro,
    resetContext,
    blockPlayingIntroOutro,
    isIntroOutroOpened,
  } = useIntroOutroContext();

  const { user, logFirebaseEvent } = useUserContext();

  const { trimmingOn, trimmingActionsBuffer } = useTrimContext();
  const { isLogoEditor } = useLogoContext();

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const {
    isBackgroundMusicOpened,
    isApplyAllTrackEnabled,
    isApplyAllVolumeEnabled,
    selectedMusic,
    musicVolume,
    videoVolume,
  } = useBackgroundMusicContext();

  const { resetVideoSpeedUpContext } = useVideoSpeedUpContext();

  const { isZoomOpened, updateZoomsSeekValue, updateZoomProgress, zoomChanged } = useZoomContext();

  const { pushScenes } = useWaitForJobsToStart({ onJobsFound, onScenesReady });

  const history = useHistory();

  const handleChanged = () => {
    setHasChanged(true);
  };

  const handleIntroOutro = useCallback(
    ({ introOutro, registerAction = false, undoOrRedoAction = false }) => {
      if (registerAction) {
        const previousIntroOutro = {};
        if (introOutro.hasOwnProperty('intro')) {
          previousIntroOutro.intro = initialIntroOutro.intro;
        }
        if (introOutro.hasOwnProperty('outro')) {
          previousIntroOutro.outro = initialIntroOutro.outro;
        }
        newIntroOutroAction({
          introOutro,
          previousIntroOutro,
        });
        changeInitialIntroOutro(introOutro);
      }

      if (undoOrRedoAction) {
        changeInitialIntroOutro(introOutro);
      }

      handleAddIntroOutro(introOutro);
    },
    [handleAddIntroOutro, videoByParts, newIntroOutroAction, initialIntroOutro]
  );

  const { pushJobs } = useSceneJobs({
    onJobFailed,
    onJobSucceed,
  });

  async function onJobSucceed(_, jobDetails) {
    const label = jobDetails.outputs[0].label;
    const sceneId = jobDetails.outputs[0].relatedTo.id;

    // If job is related to enhancing or avatar generation - check for hls job
    switch (label) {
      case ENHANCE_JOB_LABEL:
      case AVATAR_JOB_LABEL:
        // Add generated external resources to the scene
        setVideoByParts(prevState => ({
          ...prevState,
          scenes: prevState.scenes.map(item => {
            if (item.id === sceneId) {
              const resources = item.resources ?? [];
              resources.push(jobDetails.outputs[0]);
              return { ...item, resources };
            }
            return item;
          })
        }));

        const response = await VideoService.checkSceneJobs(videoId, sceneId);
        if (response.hasRunningJobs) {
          const filteredJobs = response.runningJobs.filter(jobDetails => jobDetails.outputs.length > 1 || jobDetails.outputs[0].label != 'hlsTrimmed').map(({ jobId }) => jobId);
          if (filteredJobs.length) {
            pushJobs(filteredJobs);
            return;
          }
        }
        // If no running jobs are present/only running job is hlsTrimmed, wait for scene duration
        pushScenes([{ videoId, sceneId }]);
        break;
      default:
        updateSceneResources(jobDetails.jobId, jobDetails);
    }
  }

  const {
    currentActionIndex,
    newRenameAction,
    newAddAction,
    newDeleteAction,
    newDuplicateAction,
    newMoveAction,
    newTrimAction,
    newSplitAction,
    newTextPanelAction,
    undoLastAction,
    redoLastAction,
    controlsDisabled,
    updateSceneResources: updateSceneInBufferAction,
    clearStorage,
    newIntroOutroAction,
    newTransitionAction,
    newDeleteTransitionAction,
    updateTransitionAction,
    newBackgroundMusicAction,
    newSpeedUpAction,
    newLogoAction,
    newZoomPointsAction,
  } = useEditorActions({
    updateVideoPartsScenes,
    moveScenes,
    handleChanged,
    updateIntroOutro: handleIntroOutro,
    updateTransitions,
    updateLogo: setLogo,
    updateSceneIndex: setSceneIndex,
  });

  useIsAssignmentsRead({ storyId: videoId, assignments, user, updateAssignments: setAssignments });

  function updateVideoPartsScenes(callback) {
    const updatedScenes = callback(videoByParts.scenes);
    setVideoByParts(prevState => ({ ...prevState, scenes: updatedScenes }));
  }

  function updateTransitions(callback) {
    const updatedTransitions = callback(transitions);
    console.log('updateTransitions', transitions, updatedTransitions);
    setTransitions(updatedTransitions);
  }

  function moveTransition(oldIndex, newIndex, scenes, transitions) {
    const transitionsBetweenClips = [];
    scenes.forEach((scene, index) => {
      const inBetween = newIndex < oldIndex ? index >= newIndex && index <= oldIndex : index >= oldIndex && index <= newIndex;
      if (inBetween) {
        transitions.forEach(item => {
          let isTransitionAlreadyAdded = {};
          if (item.rightMediaId === scene.id) {
            isTransitionAlreadyAdded = transitionsBetweenClips.find(transition => transition.rightMediaId === item.rightMediaId);
          } else if (item.leftMediaId === scene.id) {
            isTransitionAlreadyAdded = transitionsBetweenClips.find(transition => transition.leftMediaId === item.leftMediaId);
          }
          if (!isTransitionAlreadyAdded) {
            transitionsBetweenClips.push(item);
          }
        });
      }
    });
    const stableTransitions = transitions.filter(
      transition =>
        !transitionsBetweenClips.find(
          item => transition.rightMediaId === item.rightMediaId && transition.leftMediaId === item.leftMediaId
        )
    );
    const movedTransitions = transitionsBetweenClips.map(item => {
      const rightMediaScene = scenes.find(scene => scene.id === item.rightMediaId);
      const rightMediaSceneIndex = scenes.indexOf(rightMediaScene);
      const leftMediaScene = scenes.find(scene => scene.id === item.leftMediaId);
      const leftMediaSceneIndex = scenes.indexOf(leftMediaScene);
      if (newIndex < oldIndex) {
        if (rightMediaSceneIndex === newIndex) {
          return { ...item, rightMediaId: scenes[oldIndex].id };
        } else if (leftMediaSceneIndex === oldIndex) {
          return { ...item, leftMediaId: scenes[oldIndex - 1].id };
        } else if (leftMediaSceneIndex === newIndex) {
          const rightMediaId = scenes[newIndex].id;
          const leftMediaId = scenes[oldIndex]?.id;
          return { ...item, rightMediaId, leftMediaId };
        } else {
          const rightMediaId = scenes[leftMediaSceneIndex].id;
          const leftMediaId = scenes[leftMediaSceneIndex - 1].id;
          return { ...item, leftMediaId, rightMediaId };
        }
      } else {
        if (leftMediaSceneIndex === newIndex) {
          return { ...item, leftMediaId: scenes[oldIndex].id };
        } else if (rightMediaSceneIndex === oldIndex) {
          return { ...item, rightMediaId: scenes[oldIndex + 1].id };
        } else if (rightMediaSceneIndex === newIndex) {
          const leftMediaId = scenes[newIndex].id;
          const rightMediaId = scenes[oldIndex].id;
          return { ...item, leftMediaId, rightMediaId };
        } else {
          const leftMediaId = scenes[rightMediaSceneIndex].id;
          const rightMediaId = scenes[rightMediaSceneIndex + 1].id;
          return { ...item, leftMediaId, rightMediaId };
        }
      }
    });
    setTransitions([...stableTransitions, ...movedTransitions]);
  }

  function moveScenes(activeScene, oldIndex, newIndex, registerAction = true) {
    if (oldIndex !== newIndex) {
      // Handle transitions while moving scenes
      moveTransition(oldIndex, newIndex, videoByParts.scenes, transitions);
      const movedArray = arrayMove(videoByParts.scenes, oldIndex, newIndex);
      const activeSceneIntroOutro = activeScene.id === 'Intro' || activeScene.id === 'Outro';
      if (videoByParts.scenes?.includes(activeScene) || activeSceneIntroOutro) {
        registerAction && newMoveAction(activeScene, oldIndex, newIndex, transitions);
        const updatedIndex = movedArray.indexOf(activeScene);
        setCurrentVideoPart(updatedIndex.toString());
        const newSceneSceneIndex = getSceneIndex(updatedIndex.toString());
        setSceneIndex(newSceneSceneIndex);
      }
      setVideoByParts(prevState => ({ ...prevState, scenes: movedArray }));
    }
  }

  const handleScenesMove = useCallback(
    (oldIndex, newIndex) => {
      const activeScene = videoByParts.scenes[oldIndex];
      moveScenes(activeScene, oldIndex, newIndex, true);
    },
    [moveScenes, videoByParts]
  );

  const updateSceneData = useCallback(
    (sceneId, data, runningJobs = null, rerecord = false) => {
      if (runningJobs) {
        pushJobs(runningJobs.map(item => item.jobId));
      }
      setVideoByParts(prevState => ({
        ...prevState,
        scenes: prevState.scenes.map(item => {
          if (item.id === sceneId) {
            const dataToUpdate = {};
            if (rerecord) {
              dataToUpdate.thumbnail = null;
              dataToUpdate.video = null;
              dataToUpdate.resources = [];
              dataToUpdate.processing = true;

              if (item.enhancingDone) {
                item.enhancing = false;
                delete item.enhancingDone;
              }
            } else {
              if (!data.recording) {
                const thumbnail = ResourceHelper.getResourceByLabel(item, 'defaultStaticThumbnail');
                const stream = ResourceHelper.getResourceByLabel(item, 'hls');
                dataToUpdate.processing = !thumbnail || !stream;
                if (item.recording) {
                  ToastNotification({
                    message: t('clip_organizer.toast.draft_video.successfully_recorded'),
                    iconName: 'success',
                  });
                }
              }
            }
            return { ...item, ...data, ...dataToUpdate };
          }
          return item;
        }),
      }));
    },
    [setVideoByParts]
  );

  const getSceneIndex = videoPart => {
    if (videoByParts) {
      switch (videoPart.toString().toLowerCase()) {
        case 'intro':
          return 0;
        case 'outro':
          return videoByParts.hasOwnProperty('intro') ? videoByParts.scenes?.length + 1 : videoByParts.scenes?.length;
        default:
          return videoByParts.hasOwnProperty('intro') ? Number(videoPart) + 1 : Number(videoPart);
      }
    }
  };

  const getCurrentVideoPart = useCallback(
    index => {
      if (videoByParts) {
        const hasIntro = videoByParts.hasOwnProperty('intro');
        const hasOutro = videoByParts.hasOwnProperty('outro');
        let initialIndex = 0;
        if (hasIntro) {
          let newPart = '';
          initialIndex = 1;
          if (index === 0) {
            return 'intro';
          }
          newPart = index === videoByParts.scenes?.length + initialIndex && hasOutro ? 'outro' : (index - 1).toString();
          return newPart;
        } else {
          if (index === videoByParts.scenes?.length && hasOutro) {
            return 'outro';
          }
          return index.toString();
        }
      }
    },
    [videoByParts]
  );

  const handleSelectClip = useCallback(
    newVideoPart => {
      if (newVideoPart !== currentVideoPart) {
        const newIndex = getSceneIndex(newVideoPart);
        if (newVideoPart === 'intro' && currentVideoPart === '1' && !isIntroOutroOpened) {
          setSceneIndex(1);
          setCurrentVideoPart('0');
        } else {
          setSceneIndex(newIndex);
          setCurrentVideoPart(newVideoPart);
        }
      } else {
        setCurrentVideoPart(newVideoPart);
      }
    },
    [setCurrentVideoPart, sceneIndex, currentVideoPart, scenes, isIntroOutroOpened]
  );

  const handleEnded = useCallback(() => {
    if (!trimmingOn && !isZoomOpened) {
      if (isBackgroundMusicOpened) {
        onBackgroundMusicChange({
          sceneIndex: Number(getCurrentVideoPart(sceneIndex)),
          newMusic: selectedMusic,
          musicVolume: musicVolume / 100,
          videoVolume: videoVolume / 100,
          isApplyAllTrackEnabled,
          isApplyAllVolumeEnabled,
        });
      }

      console.log('handleEnded sceneIndex', sceneIndex);
      let newVideoIndex = sceneIndex + 1;
      if (
        scenes.length > newVideoIndex &&
        (scenes[newVideoIndex].video === null ||
          (scenes[newVideoIndex].id !== 'Intro' &&
            scenes[newVideoIndex].id !== 'Outro' &&
            scenes[newVideoIndex].resources?.length === 0))
      ) {
        // Find next scene that is not a draft
        const nextRecordedScene = Array.from(scenes)
          .slice(newVideoIndex)
          .find(item => item.video);
        newVideoIndex = nextRecordedScene ? scenes.indexOf(nextRecordedScene) : scenes.length;
      }
      if (scenes.length > newVideoIndex) {
        if (blockPlayingIntroOutro && scenes[newVideoIndex].id === 'Outro') {
          // If intro outro playing is blocked, and next scene is Outro, skip it and find next recorded scene (not a draft and not Intro)
          const nextRecordedScene = Array.from(scenes)
            .filter(item => item.id !== 'Intro')
            .find(item => item.video);
          newVideoIndex = nextRecordedScene ? scenes.indexOf(nextRecordedScene) : scenes.length;
        }
        setSceneIndex(newVideoIndex);
      } else {
        // Find 1st recorded scene in the list to avoid pointing out on draft scene
        setSceneIndex(0);
      }
    }

    if (isZoomOpened) {
      updateZoomsSeekValue(0);
      updateZoomProgress({ percents: 0, milliseconds: scenes[sceneIndex]?.trimStart });
    }
  }, [trimmingOn,
    isZoomOpened,
    sceneIndex,
    scenes,
    blockPlayingIntroOutro,
    isApplyAllTrackEnabled,
    isApplyAllVolumeEnabled,
    onBackgroundMusicChange]);

  const handleSliderChange = useCallback(
    (newVideoIndex, seekNewVideoTo, callback) => {
      if (blockPlayingIntroOutro && (scenes[newVideoIndex].id === 'Intro' || scenes[newVideoIndex].id === 'Outro')) {
        // If user seeks to intro or outro, while it's blocked, find first available scene
        const nextRecordedScene = Array.from(scenes)
          .filter(item => item.id !== 'Intro')
          .find(item => item.video);
        newVideoIndex = nextRecordedScene ? scenes.indexOf(nextRecordedScene) : scenes.length;
        seekNewVideoTo = scenes[newVideoIndex].trimStart / 1000;
      }

      if (sceneIndex !== newVideoIndex) {
        if (isBackgroundMusicOpened) {
          onBackgroundMusicChange({
            sceneIndex: Number(getCurrentVideoPart(sceneIndex)),
            newMusic: selectedMusic,
            musicVolume: musicVolume / 100,
            videoVolume: videoVolume / 100,
            isApplyAllTrackEnabled,
            isApplyAllVolumeEnabled,
          });
        }
      }
      setSceneIndex(newVideoIndex);
    },
    [
      sceneIndex,
      isBackgroundMusicOpened,
      blockPlayingIntroOutro,
      scenes,
      isApplyAllTrackEnabled,
      isApplyAllVolumeEnabled,
      onBackgroundMusicChange,
    ]
  );

  const handleCloseAllClipModal = useCallback(() => {
    setShowSelectFromLibraryModal(false);
  }, [setShowSelectFromLibraryModal]);

  const onAddClip = (index, before) => {
    setShowSelectFromLibraryModal(true);
    setClipsPosition({ index, before });
  };

  const handleAddEmptyClip = async (index, before) => {
    const newScene = {
      author: video.author,
      id: uuidv4(),
      storyId: videoId,
      sort: before ? index : index + 1,
      name: `${t('storyboard.editor.default_name')} ${before ? index + 1 : index + 2}`,
      script: '',
      narrative: '',
      video: null,
      duration: null,
      dimensions: {
        width: null,
        height: null,
      },
      isNew: true,
      isCompleted: true,
      music: null,
      orientation: video.orientation,
      originalVideo: null,
      processing: false,
      resources: [],
      subtitlesMeta: [],
      subtitles: null,
      subtitlesVisible: false,
      trimStart: 0,
      trimEnd: 0,
      trimmedDuration: 0,
      videoVolume: 1,
      waveform: null,
    };

    newAddAction([{ ...newScene }], index, before);

    insertSceneIntoClipOrganizer(newScene, index, before);

    ToastNotification({ message: t('storyboard.editor.new_scene_added'), iconName: 'success' });
    return newScene;
  };

  const insertSceneIntoClipOrganizer = useCallback(
    (scene, index, before, registerChange = true) => {
      const scenesBefore = Array.from(videoByParts.scenes).slice(0, before ? index : index + 1);
      const scenesAfter = Array.from(videoByParts.scenes).slice(before ? index : index + 1, videoByParts.scenes.length);

      setTransitions(prevState =>
        prevState.map(item => {
          if (item.leftMediaId === scenesBefore[scenesBefore.length - 1]?.id) {
            return {
              ...item,
              leftMediaId: before ? item.leftMediaId : scene.id,
              rightMediaId: before ? scene.id : scenesAfter[0]?.id,
            };
          }

          return item;
        })
      );

      setVideoByParts(prevState => ({
        ...prevState,
        scenes: [...scenesBefore, { ...scene }, ...scenesAfter],
      }));
      registerChange && setHasChanged(true);
    },
    [videoByParts]
  );

  const onUploadClipSelected = useCallback(
    async (index, before) => {
      const { result } = await VideoService.createScene({
        storyId: videoId,
        sceneId: uuidv4(),
        sort: before ? index : index + 1,
        name: `${t('storyboard.editor.default_name')} ${before ? index + 1 : index + 2}`,
      });
      console.log('result', result, structuredClone(videoByParts.scenes));
      const scene = result.find(item => !videoByParts.scenes.find(scene => scene.id === item.id));
      console.log('scene', scene);
      scene.hidden = true;
      setShowUploadModal({ show: true, sceneData: { sceneId: scene.id, storyId: videoId } });
      insertSceneIntoClipOrganizer(scene, index, before, false);
    },
    [videoByParts]
  );

  const handleCloseUploadModal = useCallback(async () => {
    if (!isClipUploading && showUploadModal?.sceneData?.sceneId) {
      const sceneToDeleteId = showUploadModal.sceneData.sceneId;
      setShowUploadModal({ show: false, sceneData: null });
      // User closes modal, clip was not uploaded, remove the hidden clip from the story
      await VideoService.deleteScene(sceneToDeleteId);
      setVideoByParts(prevState => ({ ...prevState, scenes: prevState.scenes.filter(item => item.id !== sceneToDeleteId) }));
    }
  }, [showUploadModal, isClipUploading]);

  const handleUploadClip = useCallback(
    scenes => {
      console.log('handleUploadClip', scenes);
      updateSceneData(
        showUploadModal.sceneData.sceneId,
        {
          ...scenes[0],
          hidden: false,
          processing: true,
          trimStart: timeStampToMs(scenes[0].trimStart),
          trimEnd: timeStampToMs(scenes[0].trimEnd),
        },
      );
      pushScenes(scenes.map(item => ({ videoId, sceneId: item.id })));
      setShowUploadModal({ show: false, sceneData: null });
    },
    [showUploadModal]
  );

  const handleGenerateAvatarClip = useCallback(async (sceneId, avatarId, voiceId) => {
    try {
      const { id } = await VideoService.generateAvatarClip(videoId, sceneId, avatarId, voiceId);
      // Updated already recorded scene by showing scene processing & removing scene resources
      setVideoByParts(prevState => ({
        ...prevState, scenes: prevState.scenes.map(item => {
          if (item.id === sceneId) {
            return { ...item, processing: true };
          } else {
            return item;
          }
        })
      }));
      const jobId = parseInt(id);
      setJobsBuffer(prevState => [...prevState, { jobId, scene: sceneId }]);
      pushJobs([jobId]);
    } catch (e) {
      console.error('Error generating avatar clip', e);
      ToastNotification({
        message: t('clip_organizer.generate_avatar.failed'),
      });
    }
  }, []);

  const handleCancelRecording = async () => {

    const extensionId = process.env.EXT_ID;
    const extensionExists = document.getElementById(extensionId);
    try {
      if (extensionExists) {
        window.postMessage(
          {
            type: extensionId,
            message: 'CANCEL_FROM_WEB_APP',
          },
          '*'
        );
      }
    } catch (error) {
      console.error('Error while canceling recording', error);
    } finally {
      const removeLocalStorageItem = (key) => {
        localStorage.getItem(key) && localStorage.removeItem(key);
      }
      setTimeout(() => {
        // Clear the local storage
        ['recordedStoryId', 'updateClipScript', 'userCancelDraftScene', 'isClipRecording', 'draftSceneId'].forEach((key, index) =>
          removeLocalStorageItem(key));
      }, 1000);
    }

  }

  const handleRecordScene = async ({ sceneId = null, script = '', index = 0, before = false }) => {
    const extensionId = process.env.EXT_ID;
    const extensionExists = document.getElementById(extensionId);

    let sceneIdForExtension = sceneId;
    // No sceneId means we are creating a new scene
    if (!sceneIdForExtension) {
      const { result } = await VideoService.createScene({
        storyId: videoId,
        sceneId: uuidv4(),
        sort: before ? index : index + 1,
        name: `${t('storyboard.editor.default_name')} ${before ? index + 1 : index + 2}`,
      });
      const scene = result.find(item => !videoByParts.scenes.find(scene => scene.id === item.id));
      scene.hidden = true;
      sceneIdForExtension = scene.id;
      insertSceneIntoClipOrganizer(scene, index, before, false);
      setNewlyRecordingSceneId(scene.id);
    }

    if (extensionExists) {
      window.postMessage(
        {
          type: extensionId,
          message: 'OPEN_POPUP_IN_IFRAME',
          purpose: 'draft_recording',
          sceneInfo: { sceneId: sceneIdForExtension, storyId: videoId, script: script },
        },
        '*'
      );
    } else {
      window.location = `vormats-video-storyteller://?storyId=${videoId}&sceneId=${sceneIdForExtension}`;

      setTimeout(() => {
        if (localStorage.getItem('recorderInstalled') !== 'true') {
          setIsShowExtensionModal(true);
        }
      }, 1000);
    }
  };

  function updateSceneResources(jobId, data) {
    console.log('updateSceneResources', data);
    setJobsBuffer(prevState => prevState.filter(item => item.jobId !== jobId));
    setNewResources(data);
  };

  useEffect(() => {
    if (failedJob) {
      const failedScenesId = jobsBuffer.filter(item => item.jobId === failedJob.jobId).map(item => item.scene);
      setJobsBuffer(prevState => prevState.filter(item => item.jobId !== failedJob.jobId));
      setFailedJob(null);
      if (failedJob.outputs[0].label == 'enhanced') {
        const failedScenesNames = videoByParts.scenes.filter(item => failedScenesId.find(sceneId => sceneId === item.id)).map(item => item.name);
        setVideoByParts(prevState => ({
          ...prevState,
          scenes: prevState.scenes.map(scene => {
            const failedScene = failedScenesId.find(item => scene.id === item || scene.originalId === item);
            if (failedScene) {
              scene.enhancing = false;
            }
            return scene;
          }),
        }));
        ToastNotification({
          message: t('clip_organizer.enhancing_failed_message', { name: failedScenesNames.join(', ') }),
        });
      } else if (failedJob.outputs[0].type !== 'waveform') {
        setVideoByParts(prevState => ({
          ...prevState,
          scenes: prevState.scenes.filter(scene => {
            const failedScene = failedScenesId.find(item => scene.id === item || scene.originalId === item);
            return failedScene === undefined;
          }),
        }));
        ToastNotification({
          message: t('clip_organizer.job_failed_message'),
        });
      }
    }
  }, [failedJob]);

  function onJobFailed(job) {
    setFailedJob(job);
  };

  useEffect(() => {
    if (newResources) {
      console.log('newResources', newResources);
      const updatedScenes = videoByParts.scenes?.map(item => {
        if (
          item.id.includes(newResources.outputs[0].relatedTo.id) ||
          item.originalId?.includes(newResources.outputs[0].relatedTo.id)
        ) {
          const updatedScene = { ...item };
          // Filter out old resources with same label & type as new date
          const resources = updatedScene?.resources?.filter(resource => !newResources.outputs.find(output => output.label == resource.label && output.type == resource.type)) ?? [];
          resources.push(...newResources.outputs);
          updatedScene.resources = resources;
          let waveformResource = null;
          if (newResources.outputs.find(resource => resource.type === 'waveform')) {
            waveformResource = newResources.outputs.find(resource => resource.type === 'waveform')?.url;
          }
          if (!waveformResource) {
            waveformResource = ResourceHelper.getResourceByLabel(updatedScene, 'waveform');
          }

          const thumbnailResource = ResourceHelper.getResourceByLabel(updatedScene, 'defaultStaticThumbnail');
          const hlsResource = newResources.outputs.find(resource => resource.type === 'hls');
          if (newResources.outputs.find(resource => resource.type === 'hls')) {
            if (hlsResource) {
              const audioEnhanceResource = resources.find(resource => resource.type === 'normalized' && resource.label === 'enhanced');
              if (updatedScene.enhancing) {
                updatedScene.enhancing = false;
                updatedScene.enhancingDone = Boolean(audioEnhanceResource);
              }
              updatedScene.video = `${hlsResource.url}?${Date.now()}`;
              if (thumbnailResource) {
                updatedScene.thumbnail = thumbnailResource.url;
              }
              if (!item.trimmedDuration || !item.orientation) {
                pushScenes([{ videoId: updatedScene.storyId, sceneId: newResources.outputs[0].relatedTo.id }]);
              }
            }
          }
          if (hlsResource && thumbnailResource && updatedScene.processing) {
            updatedScene.processing = false;
          }
          updateSceneInBufferAction(updatedScene);
          console.log('will return updatedScene', updatedScene);
          return updatedScene;
        } else {
          console.log('will return same scene', item);
          return item;
        }
      });
      console.log('updatedScenes', updatedScenes);
      setVideoByParts(prevState => ({ ...prevState, scenes: updatedScenes }));
    }
  }, [newResources]);

  const checkIfNeedToGenerate = (resourceType, needToGenerate, sceneJobs) => {
    if (sceneJobs.hasRunningJobs) {
      if (needToGenerate) {
        const inProcessing = sceneJobs.runningJobs.find(item => item.outputs.find(output => output.type === resourceType));
        if (inProcessing === undefined) {
          return true;
        }
        return false;
      }
    } else {
      return needToGenerate;
    }
  };

  function onJobsFound(jobsList) {
    pushJobs(jobsList);
  }

  function onScenesReady(scenesList) {
    setVideoByParts(prevState => ({
      ...prevState, scenes: prevState.scenes.map(scene => {
        const sceneWithNewData = scenesList.find(item => item.id == scene?.id || item.id == scene?.originalId);
        if (sceneWithNewData) {
          const trimStart = timeStampToMs(sceneWithNewData.trimStart);
          const trimEnd = timeStampToMs(sceneWithNewData.trimEnd);
          trimEnd > 0 && trimStart === trimEnd && Sentry.captureMessage(`onScenesReady scenes trim start and trim end are equal ${sceneWithNewData.id} trimStart=${trimStart} trimEnd=${trimEnd}`);
          const trimmedDuration = trimEnd - trimStart;
          return {
            ...scene,
            resources: sceneWithNewData.resources,
            orientation: sceneWithNewData.orientation,
            dimensions: sceneWithNewData.dimensions,
            video: ResourceHelper.getResourceByLabel(sceneWithNewData, 'hls')?.url,
            thumbnail: ResourceHelper.getResourceByLabel(sceneWithNewData, 'defaultStaticThumbnail')?.url,
            waveform: ResourceHelper.getResourceByLabel(sceneWithNewData, 'waveform')?.url,
            ...(scene.processing ? { processing: false } : {}),
            ...(scene?.trimmedDuration ? {} : {
              trimmedDuration,
              trimStart,
              trimEnd,
            })
          };
        }
        return scene;
      })
    }));
  }

  const checkHlsResourceGenerated = async (videoId, scene) => {
    const operatedScene = { ...scene };
    if (scene?.originalId) {
      operatedScene.id = scene?.originalId;
    }
    const hasHls = ResourceHelper.getResourceByLabel(scene, 'hls');
    const response = await VideoService.checkSceneJobs(videoId, operatedScene.id);
    if (!hasHls && response.hasRunningJobs) {
      const jobsToCheck = [];
      response.runningJobs.forEach(jobDetails => {
        if (jobDetails.outputs.length > 1 || jobDetails.outputs[0].label != 'hlsTrimmed') {
          jobsToCheck.push(jobDetails.jobId)
        }
      });
      pushJobs(jobsToCheck);
    } else {
      if (!hasHls) {
        pushScenes([{ videoId, sceneId: scene?.originalId ?? scene?.id }]);
      }
    }
  };

  const handleSaveSelectedClips = useCallback(
    async selectedClips => {
      const clips = Array.isArray(selectedClips) ? Array.from(selectedClips) : [selectedClips];
      const addedClips = await Promise.all(
        clips.map(async clip => {
          clip.trimStart = timeStampToMs(clip.trimStart);
          clip.trimEnd = timeStampToMs(clip.trimEnd);
          clip.trimEnd > 0 && clip.trimStart === clip.trimEnd && Sentry.captureMessage(`handleSaveSelectedClips scenes trim start and trim end are equal ${clip.id}, trimStart=${clip.trimStart}, trimEnd=${clip.trimEnd}`);
          clip.music = isApplyAllTrackEnabled && selectedMusic ? { ...selectedMusic, volume: musicVolume / 100 } : null;

          clip.backgroundMusicChanged = isApplyAllTrackEnabled || isApplyAllVolumeEnabled;

          if (isApplyAllVolumeEnabled) {
            clip.videoVolume = videoVolume / 100;
          }

          const trimmedDuration = clip.trimEnd - clip.trimStart;
          const waveform = ResourceHelper.getResourceByLabel(clip, 'waveform');
          const thumbnail = ResourceHelper.getResourceByLabel(clip, 'defaultStaticThumbnail');
          const stream = ResourceHelper.getResourceByLabel(clip, 'hls');
          if (clip.orientation === null && thumbnail && stream) {
            clip.orientation = clip?.storyOrientation ?? 'landscape';
          }
          stream == undefined && checkHlsResourceGenerated(clip.storyId, clip, updateSceneResources, onJobFailed);
          clip.processing = clip.video && (!thumbnail || !stream);
          if (!clip.video) {
            clip.isNew = true;
          }
          if (clip.video) {
            clip.video = stream?.url ?? '';
          }
          if (!clip.thumbnail && thumbnail?.url) {
            clip.thumbnail = thumbnail?.url;
          }
          return {
            ...clip,
            originalId: clip.id,
            id: uuidv4(),
            trimmedDuration,
            waveform: waveform?.url,
            isCompleted: true,
          };
        })
      );
      newAddAction(addedClips, clipsPosition.index, clipsPosition.before);
      // Rely on shareModal index and before flag insert stories
      const scenesBefore =
        clipsPosition.index === 0 && clipsPosition.before
          ? []
          : Array.from(videoByParts.scenes).slice(0, clipsPosition.before ? clipsPosition.index : clipsPosition.index + 1);
      const scenesAfter = Array.from(videoByParts.scenes).slice(
        clipsPosition.before ? clipsPosition.index : clipsPosition.index + 1,
        videoByParts.scenes.length
      );
      if (scenesBefore.length > 0 && scenesAfter.length > 0) {
        setTransitions(prevState =>
          prevState.map(item => {
            if (item.leftMediaId === scenesBefore[scenesBefore.length - 1].id) {
              let leftMediaId = item.leftMediaId;
              let rightMediaId = addedClips[0].id;
              if (!clipsPosition.before) {
                rightMediaId = scenesAfter[0].id;
                leftMediaId = addedClips[0].id;
              }
              return { ...item, leftMediaId, rightMediaId };
            }
            return item;
          })
        );
      }
      setVideoByParts(prevState => ({ ...prevState, scenes: [...scenesBefore, ...addedClips, ...scenesAfter] }));
      setClipsPosition({ index: 0, before: false });
    },
    [videoByParts, clipsPosition, newAddAction, setClipsPosition, setTransitions]
  );

  const handleUpdateVideo = useCallback(
    (video = null) => {
      const sortedScenes = [...video.scenes].sort((a, b) => a.sort - b.sort);
      video.transitions && setTransitions(video.transitions);
      setVideo({ ...video, scenes: sortedScenes });
    },
    [setVideo]
  );

  /**
   * Intro/Outro thumbnails use a naming convention where they have the same path as the video, but
   * are stored in the static resources bucket and have a .jpg extension
   * @param {string} videoUrl
   */
  const getThumbnailPathFromVideoUrl = videoUrl => {
    if (!videoUrl) {
      return undefined;
    }

    let url = new URL(decodeURI(videoUrl));
    let pathParts = url.pathname.split('.');
    let path = pathParts[0] + '.jpg';

    let thumbnailPath = `https://${config.staticResourcesBucket}.s3.amazonaws.com${path}`;

    console.log('thumbnailPath', thumbnailPath);

    return thumbnailPath;
  };

  /**
   * Intro/Outro streams use a naming convention where they have the same path as the video, but
   * are stored in the static resources bucket and have a .m3u8 extension
   * @param {string} videoUrl
   */
  const getHlsPathFromVideoUrl = videoUrl => {
    if (!videoUrl) {
      return undefined;
    }

    let url = new URL(decodeURI(videoUrl));
    let pathParts = url.pathname.split('.');
    let path = pathParts[0] + '.m3u8';

    let hlsPath = `https://${config.staticResourcesBucket}.s3.amazonaws.com${path}`;

    console.log('hlsPath', hlsPath);

    return hlsPath;
  };

  async function handleAddIntroOutro(introOutro) {
    chooseIntroOutro(introOutro);

    if (introOutro.hasOwnProperty('intro')) {
      const { intro } = introOutro;
      if (intro?.video) {
        const streamPath = getHlsPathFromVideoUrl(intro?.video);
        setVideoByParts(prevState => ({
          ...prevState,
          intro: {
            author: {},
            video: streamPath || intro?.video,
            url: intro?.video,
            thumbnail: intro?.thumbnail,
            id: 'Intro',
            trimStart: 0,
            trimEnd: intro?.duration,
            trimmedDuration: intro?.duration,
            isCompleted: true,
            duration: intro?.duration,
          },
        }));
      } else {
        setVideoByParts(prevState => {
          const { intro, ...newParts } = prevState;
          return newParts;
        });
      }
      if (currentVideoPart !== 'intro' && intro?.video) {
        handleSelectClip('intro');
      }
    }

    if (introOutro.hasOwnProperty('outro')) {
      const { outro } = introOutro;
      if (outro?.video) {
        const streamPath = getHlsPathFromVideoUrl(outro?.video);
        setVideoByParts(prevState => ({
          ...prevState,
          outro: {
            author: {},
            video: streamPath || outro?.video,
            url: outro?.video,
            thumbnail: outro?.thumbnail,
            id: 'Outro',
            trimStart: 0,
            trimEnd: outro?.duration,
            trimmedDuration: outro?.duration,
            isCompleted: true,
            duration: outro?.duration,
          },
        }));
      } else {
        setVideoByParts(prevState => {
          const { outro, ...newParts } = prevState;
          return newParts;
        });
      }
    }
    if ((currentVideoPart === 'outro' && !introOutro?.outro?.video) || (currentVideoPart === 'intro' && !introOutro?.intro?.video)) {
      console.log('reset currentVideoPart to 0');
      handleSelectClip('0');
    }
  }

  const onReloadEvent = event => {
    const e = event || window.event;

    if (showUploadModal?.sceneData?.sceneId) {
      VideoService.deleteScene(showUploadModal.sceneData.sceneId);
      setShowUploadModal({ show: false, sceneData: null });
    }

    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  const handleSceneRename = useCallback(
    (renamedScene, changes) => {
      newRenameAction(renamedScene, changes);
      videoByParts &&
        setVideoByParts(prevState => ({
          ...prevState,
          scenes: prevState.scenes?.map(scene => (scene.id === renamedScene.id ? { ...scene, ...changes } : scene)),
        }));
    },
    [videoByParts, setVideoByParts, newRenameAction]
  );

  const handleDeleteScene = useCallback(
    (sceneToDelete, index) => {
      const sceneTransitions = transitions.filter(
        item => item.rightMediaId === sceneToDelete.id || item.leftMediaId === sceneToDelete.id
      );
      newDeleteAction(sceneToDelete, index, sceneTransitions);

      const existingScenes = videoByParts.scenes?.filter(item => item.id !== sceneToDelete.id);

      // Check if scene was deleted
      if (videoByParts.scenes.length > existingScenes.length) {
        const currentIndex = getSceneIndex(currentVideoPart);

        // If the deleted scene had an index less than the current scene,
        // then it is required to decrease the index of the current scene by 1,
        // since the location of the scene in the array has shifted by 1 to the beginning
        if (currentIndex !== 0 && index <= currentIndex) {
          setSceneIndex(getSceneIndex(currentVideoPart) - 1);
        }

        // If the current scene is deleted, then the video is paused
        // and need to turn off the background music too
        if (index === currentIndex) {
          setIsStopBackgroundMusic(true);
        }
      }
      setTransitions(prevState =>
        prevState.filter(item => item.rightMediaId !== sceneToDelete.id && item.leftMediaId !== sceneToDelete.id)
      );

      videoByParts &&
        setVideoByParts(prevState => ({
          ...prevState,
          scenes: prevState.scenes?.filter(item => item.id !== sceneToDelete.id),
        }));
    },
    [currentVideoPart, newDeleteAction, setVideoByParts, videoByParts]
  );

  const handleTrimScene = useCallback(
    (index, trimStart, trimEnd) => {
      console.log('handleTrimScene', index, trimStart, trimEnd);
      const updatedScenes = [...videoByParts.scenes];
      if (updatedScenes[index].trimStart !== trimStart || updatedScenes[index].trimEnd !== trimEnd) {
        newTrimAction({ ...updatedScenes[index] }, trimStart, trimEnd);
        updatedScenes[index].trimStart = trimStart;
        updatedScenes[index].trimEnd = trimEnd;
        trimEnd > 0 && trimStart === trimEnd && Sentry.captureMessage(`Trim start and trim end are equal trimStart=${trimStart}, trimEnd=${trimEnd} ${updatedScenes[index].id}`);
        updatedScenes[index].trimmedDuration = trimEnd - trimStart;
        updatedScenes[index].trimmed = true;
        setVideoByParts(prevState => ({ ...prevState, scenes: updatedScenes }));
      }
    },
    [videoByParts, setVideoByParts, newTrimAction, transitions]
  );

  const handleSplitScene = useCallback(
    (index, value, newTrimStart, newTrimEnd) => {
      console.log('handleSplitScene', index, value);
      const leftScene = structuredClone(videoByParts.scenes[index]);
      if (value !== leftScene.trimStart && value !== leftScene.trimEnd) {
        const rightScene = structuredClone(videoByParts.scenes[index]);
        if (!rightScene?.originalId) {
          rightScene.originalId = rightScene.id;
        }
        rightScene.id = uuidv4();
        rightScene.trimStart = value;
        rightScene.trimEnd = newTrimEnd;
        rightScene.trimmedDuration = rightScene.trimEnd - rightScene.trimStart;
        rightScene.trimmed = true;
        rightScene.trimEnd > 0 && rightScene.trimStart === rightScene.trimEnd && Sentry.captureMessage(`Split for scene ${videoByParts.scenes[index].id}: right scenes trim start and trim end are equal newTrimEnd=${newTrimEnd} trimStart=${value}`);
        newSplitAction(index, value, leftScene.trimStart, leftScene.trimEnd, rightScene, newTrimStart, newTrimEnd);
        leftScene.trimStart = newTrimStart;
        leftScene.trimEnd = value;
        leftScene.trimmedDuration = leftScene.trimEnd - leftScene.trimStart;
        leftScene.trimmed = true;
        leftScene.trimEnd > 0 && leftScene.trimStart === leftScene.trimEnd && Sentry.captureMessage(`Split for scene ${videoByParts.scenes[index].id}: left scenes trim start and trim end are equal trimEnd=${value} newTrimStart=${newTrimStart}`);


        const scenesBefore = videoByParts.scenes.slice(0, index);
        const scenesAfter = videoByParts.scenes.slice(index + 1, videoByParts.scenes.length);
        setVideoByParts(prevState => ({ ...prevState, scenes: [...scenesBefore, leftScene, rightScene, ...scenesAfter] }));

        setTransitions(prevState =>
          prevState.map(item => {
            if (item.leftMediaId === leftScene?.id) {
              let leftMediaId = rightScene.id;
              return { ...item, leftMediaId };
            }
            return item;
          })
        );
      }
    },
    [videoByParts, setVideoByParts, newSplitAction]
  );

  const handleSaveTextPanel = useCallback(
    (sceneIndex, topText, bottomText, template = null) => {
      const isNull = !topText?.trim() && !bottomText?.trim();
      if (isNull || (topText !== '' && topText?.trim().length > 0) || (bottomText !== '' && bottomText?.trim().length > 0)) {
        newTextPanelAction(sceneIndex, topText, bottomText, videoByParts.scenes[sceneIndex]?.nameCard);
        setVideoByParts(prevState => ({
          ...prevState,
          scenes: prevState.scenes.map((item, index) =>
            index === sceneIndex
              ? {
                ...item,
                textPanelChanged: true,
                nameCard: isNull ? null : { topText: topText?.trimEnd(), bottomText: bottomText?.trimEnd(), templateId: template?.id, template, selectedDate: Date.now() },
              }
              : item
          ),
        }));
      }
    },
    [videoByParts, setVideoByParts, newTextPanelAction]
  );

  const handleDuplicateScene = useCallback(
    sceneToDuplicate => {
      const duplicatedId = sceneToDuplicate?.originalId ?? sceneToDuplicate.id;
      const newScene = { ...sceneToDuplicate, id: uuidv4() };
      if (!sceneToDuplicate.isNew) {
        newScene.originalId = duplicatedId;
      }
      logFirebaseEvent('co_dup_cl', { story_id: videoId, scene_id: sceneToDuplicate.id });

      newDuplicateAction(newScene, scenes?.length);
      setVideoByParts(prevState => ({ ...prevState, scenes: [...prevState.scenes, newScene] }));
    },
    [scenes, setVideoByParts, setVideoByParts]
  );

  const handleSavingSucceed = () => {
    // If update success, show toaster
    ToastNotification({
      message: t('clip_organizer.toast.successfully_saved'),
      iconName: 'success',
    });
    setHasChanged(false);
    setSaving(false);
    clearStorage();
  };

  const onAddTransition = useCallback(
    (sceneIndex, before) => {
      let rightMediaId = '';
      let leftMediaId = '';
      if (before) {
        rightMediaId = videoByParts.scenes[sceneIndex]?.id;
        leftMediaId = videoByParts.scenes[sceneIndex - 1]?.id;
      } else {
        rightMediaId = videoByParts.scenes[sceneIndex + 1]?.id;
        leftMediaId = videoByParts.scenes[sceneIndex]?.id;
      }
      const newTransition = {
        type: TRANSITION_TYPE.DIP_TO_WHITE,
        durationMs: 1000,
        leftMediaId,
        rightMediaId,
      };
      setTransitions(prevState => [...prevState, newTransition]);
      newTransitionAction(newTransition);
    },
    [newTransitionAction, setTransitions, videoByParts, transitions]
  );

  const onDeleteTransition = useCallback(
    rightMediaId => {
      const transitionToDelete = transitions.find(item => item.rightMediaId === rightMediaId);
      setTransitions(prevState => prevState.filter(item => item.rightMediaId !== rightMediaId));
      newDeleteTransitionAction(transitionToDelete);
    },
    [transitions, setTransitions, newDeleteTransitionAction]
  );

  const onTransitionChange = useCallback(
    (rightMediaId, newTransitionType) => {
      const transitionType = transitions.find(item => item.rightMediaId === rightMediaId)?.type;
      setTransitions(prevState =>
        prevState.map(item => (item.rightMediaId === rightMediaId ? { ...item, type: newTransitionType } : item))
      );
      updateTransitionAction(rightMediaId, transitionType, newTransitionType);
    },
    [transitions, setTransitions, updateTransitionAction]
  );

  const onBackgroundMusicChange = useCallback(
    ({ sceneIndex, newMusic, musicVolume, videoVolume, isApplyAllTrackEnabled, isApplyAllVolumeEnabled, isTemporaryAction }) => {
      if (!isTemporaryAction) {
        newBackgroundMusicAction({
          sceneIndex,
          newMusic,
          musicVolume,
          videoVolume,
          isApplyAllTrackEnabled,
          isApplyAllVolumeEnabled,
          previousScenes: videoByParts.scenes,
        });
      }

      setVideoByParts(prevState => ({
        ...prevState,
        scenes: prevState.scenes.map((item, index) => {
          if (index === sceneIndex) {
            return {
              ...item,
              backgroundMusicChanged: true,
              music: newMusic ? { ...newMusic, volume: musicVolume } : null,
              videoVolume,
            };
          }

          // We are forced to do this checks, because when the 'Apply to all' for tracks turned off
          // and the 'Apply to all' for track's volume is turned on,
          // the music doesn't change, but music's volume changes,
          // and the field of which is located inside the music object should be changed without changing music itself

          const initialMusicVolume = 0.5;

          const newAppliedMusic = newMusic
            ? {
              ...newMusic,
              volume: isApplyAllVolumeEnabled ? musicVolume : item?.music?.volume ?? initialMusicVolume,
            }
            : null;
          const currentMusic = item.music
            ? {
              ...item.music,
              volume: isApplyAllVolumeEnabled ? musicVolume : item?.music?.volume ?? initialMusicVolume,
            }
            : null;

          return {
            ...item,
            backgroundMusicChanged: item.backgroundMusicChanged || isApplyAllTrackEnabled || isApplyAllVolumeEnabled,
            music: isApplyAllTrackEnabled ? newAppliedMusic : currentMusic,
            ...(isApplyAllVolumeEnabled && { videoVolume }),
          };
        }),
      }));
    },
    [videoByParts, setVideoByParts, newBackgroundMusicAction]
  );

  const handleSpeedUpChange = useCallback(
    ({ sceneIndex, playbackRate, initialSpeedUpValue, registerAction = false }) => {
      if (registerAction) {
        newSpeedUpAction({ sceneIndex, previousPlaybackRate: initialSpeedUpValue, playbackRate });
      }

      setVideoByParts(prevState => ({
        ...prevState,
        scenes: prevState.scenes?.map((scene, index) =>
          index === sceneIndex ? { ...scene, playbackRate, speedUpChanged: scene?.speedUpChanged || registerAction } : scene
        ),
      }));
    },
    [videoByParts, setVideoByParts, newSpeedUpAction]
  );

  const handleLogoChange = useCallback(
    (newLogo, registerAction = false) => {
      if (registerAction) {
        newLogoAction({ logo: newLogo, prevLogo: logo });
      }

      setLogo(newLogo);
    },
    [logo, setLogo, newLogoAction]
  );

  const handleZoomPointsChange = useCallback(
    ({ sceneIndex, zoomPoints, initialZoomPoints, registerAction = false }) => {
      if (registerAction) {
        newZoomPointsAction({ sceneIndex, previousZoomPoints: initialZoomPoints, zoomPoints });
      }

      setVideoByParts(prevState => ({
        ...prevState,
        scenes: prevState.scenes?.map((scene, index) =>
          index === sceneIndex ? { ...scene, zoomPoints, zoomPointsChanged: scene?.zoomPointsChanged || registerAction } : scene
        ),
      }));
    },
    [videoByParts, setVideoByParts, newZoomPointsAction]
  );

  let isObjectsArraysDifferent = (firstArray, secondArray) => {
    return !(
      firstArray.length === secondArray.length &&
      firstArray.every(firstArrayItem =>
        secondArray.some(secondArrayItem => Object.keys(firstArrayItem).every(key => firstArrayItem[key] === secondArrayItem[key]))
      )
    );
  };

  const uploadTextPanelsForScenes = async (videoId, scenes) => {
    if (scenes.length) {
      // First things first - upload text panels images to s3 using presigned urls
      // Find if any scene has edited text panel, except one that were removed
      const failedIds = await uploadScenesTextPanelsPng(videoId, scenes);
      // If some url uploading failed, exlude text panel for the scene from saving request
      if (failedIds.length) {
        // Map scenes array to disable textPanelChanged for scenes that failed to upload urls
        ToastNotification({
          message: parseString(
            t('clip_organizer.saving.failed_to_upload_text_panels', {
              scenes: scenes
                .filter(item => failedTextPanels.find(id => id === item.id))
                .map(item => `'${item.name}'`)
                .join(', '),
            })
          ),
        });
        return failedIds;
      }
      return [];
    }
  }

  const saveEdits = async () => {
    // Show loading while saving changes
    setSaving(true);

    const { intro, outro } = videoByParts;

    let scenes = structuredClone(videoByParts.scenes);

    if (!scenes || scenes?.length < 1) {
      // Error
      ToastNotification({
        message: t('clip_organizer.toast.scene_missing'),
      });
      setHasChanged(false);
      setSaving(false);
      return;
    }
    let videoDetails = { ...video };
    const changedOriginalScenes = scenes.filter(item => item.textPanelChanged && item.nameCard && !item.hasOwnProperty('originalId'));
    const changedDuplicatedScenes = scenes.filter(item => item.textPanelChanged && item.nameCard && item.hasOwnProperty('originalId'));

    // Firstly generate and upload text panels for scenes that are ALREADY in the video 
    // For duplicated scenes, that has text panel changes, upload after story update request
    if (changedOriginalScenes.length) {
      const failedIds = uploadTextPanelsForScenes(video.id, changedOriginalScenes);
      if (failedIds.length) {
        scenes = scenes.map(item => (failedIds.find(id => id === item.id) ? { ...item, textPanelChanged: false } : item));
      }
    }

    try {
      // Array of scene IDs
      let scenesToUpdate = scenes?.map(scene => {
        const sceneInfo = { name: scene.name, id: scene.id };
        if (scene.hasOwnProperty('originalId')) {
          sceneInfo.id = scene.originalId;
          sceneInfo.newSceneId = scene.id;
        }
        if (scene.isNew) {
          sceneInfo.newSceneId = scene.id;
          sceneInfo.orientation = scene.orientation;
        }

        return sceneInfo;
      });

      // Check if any scene was added/removed or the scenes order was changed
      let isScenesSequenceChanged = scenes.length !== video.scenes.length;
      !isScenesSequenceChanged &&
        scenes.forEach((item, index) => {
          if (item?.id !== video.scenes[index]?.id) {
            isScenesSequenceChanged = true;
          }
        });

      let currentSceneIds = video.scenes?.map(obj => obj.id);
      let toBeDeleted = currentSceneIds.filter(x => !scenesToUpdate.map(item => item.id).includes(x));
      const isNewIntroOutro =
        video.introMeta?.introOutroId !== initialIntroOutro.intro?.id || video.outroMeta?.introOutroId !== initialIntroOutro.outro?.id;

      let updateTransitions = isObjectsArraysDifferent(video.transitions, transitions);
      // Only until backend fixed and we're using story update endpoint for scenes
      const sceneWithUpdatedMusic = scenes.find(item => item.backgroundMusicChanged);
      const logoUrl = logo?.path ?? logo?.url ?? null;
      const isLogoChanged = logoUrl?.split('?')[0] !== video.logo?.split('?')[0];

      // Check if scenes fields was changed
      const scenesChanges = scenes
        .filter(
          item =>
            item.backgroundMusicChanged ||
            item.trimmed ||
            item.textPanelChanged ||
            item.nameChanged ||
            item.narrativeChanged ||
            item.scriptChanged ||
            item.speedUpChanged ||
            item.zoomPointsChanged
        )
        .map(scene => {
          const {
            backgroundMusicChanged,
            trimmed,
            textPanelChanged,
            nameChanged,
            narrativeChanged,
            scriptChanged,
            speedUpChanged,
            zoomPointsChanged,
          } = scene;
          const sceneInfo = { storyId: videoId, id: scene.id };

          if (backgroundMusicChanged) {
            sceneInfo.musicId = scene.music?.id || null;

            if (scene.music) {
              sceneInfo.musicVolume = scene.music.volume;
            }

            sceneInfo.videoVolume = scene.videoVolume;
          }

          if (trimmed) {
            sceneInfo.trimRanges = [
              {
                from: scene.trimStart,
                to: scene.trimEnd,
              },
            ];
          }

          if (textPanelChanged) {
            // When text panel is removed, textPanel field should be null
            sceneInfo.textPanel = {};
            if (scene.nameCard) {
              sceneInfo.textPanel = {
                bottomText: scene.nameCard.bottomText,
                topText: scene.nameCard.topText,
                templateId: scene.nameCard.templateId,
              };
            }
          }

          if (nameChanged) {
            sceneInfo.name = scene.name;
          }

          if (narrativeChanged) {
            sceneInfo.narrative = scene.narrative;
          }

          if (scriptChanged) {
            sceneInfo.script = scene.script;
          }

          if (speedUpChanged) {
            sceneInfo.playbackRate = scene.playbackRate;
          }

          if (zoomPointsChanged) {
            sceneInfo.zoomPoints = scene.zoomPoints.map(({ id, ...rest }) => rest);
          }

          return sceneInfo;
        });

      if (
        userPermissions.updateStory &&
        (isScenesSequenceChanged ||
          isNewIntroOutro ||
          updateTransitions ||
          video.source === 'mobile' ||
          scenesChanges.length > 0 ||
          isLogoChanged)
      ) {
        const payload = { source: 'web' };
        if (isNewIntroOutro) {
          payload.intro = intro ? intro.url : null;
          payload.outro = outro ? outro.url : null;
        }
        // If we're about to delete a scene permanently, show message in UI
        if (toBeDeleted.length > 0) {
          const deletedScenesNames = toBeDeleted.map(item => video.scenes.find(videScenes => item.includes(videScenes.id)));
          const confirmScenesRemove = confirm(
            `${t('clip_organizer.confirg_scenes_delete')} ${deletedScenesNames.map(item => `"${item.name}"`).join(', ')}`
          );
          if (!confirmScenesRemove) {
            setSaving(false);
            return;
          }
        }
        if (isScenesSequenceChanged) {
          payload.scenes = scenesToUpdate;
        }
        if (updateTransitions) {
          payload.transitions = transitions;
        }

        let runMusicJob = scenesChanges.length === 0 ? true : !(sceneWithUpdatedMusic && sceneWithUpdatedMusic.music);
        // Only until backend fixed and we're using story update endpoint for scenes

        if (isLogoChanged) {
          payload.logo = logoUrl;
        }

        // Save changes in story table in db
        await VideoService.updateVideo(video.id, { ...payload }, runMusicJob ? Number(runMusicJob) : null);

        if (changedDuplicatedScenes.length) {
          const failedIds = uploadTextPanelsForScenes(videoId, changedDuplicatedScenes);
          if (failedIds.length) {
            scenesChanges = scenesChanges.map(item => {
              if (failedIds.find(id => id === item.id)) {
                if (item.backgroundMusicChanged ||
                  item.trimmed ||
                  item.nameChanged ||
                  item.narrativeChanged ||
                  item.scriptChanged ||
                  item.speedUpChanged ||
                  item.zoomPointsChanged) {
                  return { ...item, textPanelChanged: false }
                } else {
                  // If no other changes - exclude scene from saving request
                  return null;
                }
              }
              return item;
            }).filter(item => item !== null);
          }
        }
      }

      if (scenesChanges.length > 0) {
        await CmsAPI.bulkUpdateScenes(scenesChanges);
      }

      videoDetails = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
      const rawConfig = await VideoService.getStoryConfig(videoId);
      const preparedConfig = await assignFontsForConfig(rawConfig);
      setVideoConfig(preparedConfig);
      handleUpdateVideo(videoDetails);

      handleSavingSucceed();
    } catch (e) {
      // If update success, show toaster
      ToastNotification({
        message: t('clip_organizer.toast.error_while_saving'),
      });
      console.log('Error while saving changes:', e);
      setSaving(false);
    }
  };

  const handleUpdateScene = async (id, changes) => {
    try {
      setHasChanged(true);
      setVideoByParts(prevState => ({
        ...prevState,
        scenes: prevState.scenes?.map(scene => {
          if (scene.id === id) {
            // For text fields check if they are the same as before and if yes, remove them from changes
            const textFieldsCheck = ['name', 'narrative', 'script'];

            textFieldsCheck.forEach(field => {
              if (changes.hasOwnProperty(field)) {
                if (changes[field] === scene[field]) {
                  for (var key in changes) if (key.toLowerCase().includes(field)) delete changes[key];
                }
              }
            })

            return { ...scene, ...changes };
          }
          return scene;
        }),
      }));
    } catch (e) {
      console.log(`Update changes ${changes} fro scene ${id} failed with code`, e);
    }
  };

  const handleCompleteAssignment = useCallback(async () => {
    try {
      await VideoService.completeAssignment(videoId, { completed: true });
      history.push('/eg/home');

      ToastNotification({
        message: t('clip_organizer.toast.project_successfully_done'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (err) {
      ToastNotification({
        message: t('clip_organizer.toast.error_while_saving'),
        isMobileView: isMobileView,
      });
    }
  }, []);

  const onEnhanceScene = async (sceneId) => {
    setVideoByParts(prevState => ({
      ...prevState,
      scenes: prevState.scenes.map(scene => (scene.id === sceneId ? { ...scene, enhancing: true } : scene)),
    }));
    const { id } = await VideoService.enhanceSceneAudio(videoId, sceneId);
    const jobId = parseInt(id);
    setJobsBuffer(prevState => [...prevState, { jobId, scene: sceneId }]);
    pushJobs([jobId]);
  }

  const onRemoveEnhanceScene = async (sceneId) => {
    setVideoByParts(prevState => ({
      ...prevState,
      scenes: prevState.scenes.map(scene => (scene.id === sceneId ? { ...scene, enhancing: true, resources: scene.resources.filter(resource => resource.label !== 'enhanced') } : scene)),
    }));

    await VideoService.removeEnhanceScene(videoId, sceneId);
    const result = await VideoService.checkSceneJobs(videoId);
    if (result.hasRunningJobs) {
      const runningJobId = result.runningJobs[0].jobId;
      setJobsBuffer(prevState => [...prevState, { jobId: runningJobId, scene: sceneId }]);
      pushJobs([runningJobId]);
    }
  }

  const handleCompleteAssignments = useCallback(async () => {
    try {
      await VideoService.completeAssignments(videoId, { completed: true });
      history.push(`/eg/video/${videoId}`);

      ToastNotification({
        message: t('clip_organizer.toast.project_successfully_completed'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (err) {
      ToastNotification({
        message: t('clip_organizer.toast.error_while_saving'),
        isMobileView: isMobileView,
      });
    }
  }, []);

  const handleReviseAssignment = useCallback(
    async assignee => {
      try {
        await VideoService.updateUserAssignmentsCompletion(videoId, assignee.username, { completed: false });
        const assignments = await VideoService.getVideoAssignmentsList(videoId);
        setAssignments(assignments);
        ToastNotification({
          message: t('clip_organizer.toast.assignments_successfully_revised', { userName: assignee.name }),
          iconName: 'success',
        });
      } catch (err) {
        ToastNotification({
          message: t('clip_organizer.toast.error_while_saving'),
        });
      }
    },
    [videoId, user]
  );

  useEffect(() => {
    if (hasChanged || showUploadModal?.show) {
      window.addEventListener('beforeunload', onReloadEvent);
    } else {
      window.removeEventListener('beforeunload', onReloadEvent);
    }

    return () => window.removeEventListener('beforeunload', onReloadEvent);
  }, [hasChanged, showUploadModal]);

  useEffect(() => {
    if (trimmingOn) {
      hasChanged && window.removeEventListener('beforeunload', onReloadEvent);
    } else {
      if (hasChanged) {
        window.addEventListener('beforeunload', onReloadEvent);
      }
    }
  }, [trimmingOn]);

  useEffect(() => {
    // If we're generating HLS and/or thumbnails, wait until all jobs are done
    async function getStoryDetails() {
      // Check if scenes have HLS & thumbnails, if not, show loading until done
      let story = {};
      if (initialVideoDetails) {
        story = { ...initialVideoDetails };
      } else {
        const result = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
        story = { ...result };
      }
      const assignments = await VideoService.getVideoAssignmentsList(videoId);
      setAssignments(assignments);
      handleUpdateVideo(story);
    }
    getStoryDetails();
  }, []);

  useEffect(() => {
    if (initialVideoConfig) {
      setVideoConfig(initialVideoConfig);
    }
  }, [initialVideoConfig]);


  useEffect(() => {
    if (videoConfig && !init) {
      setLogo(videoConfig.logo);
    }
  }, [init, videoConfig])

  useEffect(() => {
    const isIntroOutroSelected = currentVideoPart === 'intro' || currentVideoPart === 'outro';

    if (isIntroOutroSelected && isLogoEditor) {
      handleSelectClip('0');
    }
  }, [currentVideoPart, isLogoEditor]);

  useEffect(() => {
    const initVideo = async () => {
      console.log('initVideo');
      const { readStory, updateStory, allowedScenes } = userPermissions;
      userPermissions.isOwner = video.author.username === user.username;
      userPermissions.isFolderEditor =
        storyRole === STORY_USER_RELATION.FOLDER_EDITOR.TYPE ||
        storyRole === STORY_USER_RELATION.FOLDER_ADVANCED_EDITOR.TYPE ||
        storyRole === STORY_USER_RELATION.FOLDER_OWNER.TYPE;

      if (!readStory && allowedScenes.length === 0) {
        history.push('/eg/dashboard');
      }
      const scenesToOperate =
        readStory || updateStory
          ? structuredClone([...video.scenes])
          : video.scenes.filter(item => allowedScenes.find(allowed => item.id === allowed));

      const result = await VideoService.checkSceneJobs(videoId);
      const generatedScenes = scenesToOperate
        .sort((a, b) => a.sort - b.sort)
        .map(scene => {
          const isSceneDraft = scene.trimmedDuration === 0;
          const thumbnail = ResourceHelper.getResourceByLabel(scene, 'defaultStaticThumbnail');
          const stream = ResourceHelper.getResourceByLabel(scene, 'hls');
          const waveform = ResourceHelper.getResourceByLabel(scene, 'waveform');
          scene.originalVideo = scene.video;
          scene.trimStart = timeStampToMs(scene.trimStart);
          scene.trimEnd = timeStampToMs(scene.trimEnd);
          scene.trimEnd > 0 && scene.trimStart === scene.trimEnd && Sentry.captureMessage(`Init editor scenes trim start and trim end are equal ${scene.id}, trimStart=${scene.trimStart}, trimEnd=${scene.trimEnd}`);
          const author = assignments.length
            ? assignments.find(item => item.sceneId === scene.id)?.assigned ?? video.author
            : video.author;

          const isCompleted = assignments.length
            ? assignments.find(item => item.sceneId === scene.id)
              ? Boolean(assignments.find(item => item.sceneId === scene.id).completedAt)
              : true
            : true;

          let processing = isSceneDraft ? false : !thumbnail || !stream;
          let enhancing = false;

          const jobsToCheck = [];
          if (result?.hasRunningJobs) {
            // Check for external jobs running
            EXTERNAL_JOBS_LIST.forEach(jobLabel => {
              const externalJob = result.runningJobs.find(jobData => jobData.outputs.find(output => output.relatedTo.id === scene.id && output.label == jobLabel) !== undefined);
              if (externalJob) {
                // State for scene
                switch (jobLabel) {
                  case ENHANCE_JOB_LABEL:
                    enhancing = true;
                    break;
                  default:
                    processing = true;
                }
                jobsToCheck.push(externalJob.jobId);
              } else {
                // If external job is already run, so scene has a resource, check for hls job
                if (ResourceHelper.getResourceByLabel(scene, jobLabel) !== undefined) {
                  const sceneHlsJob = result.runningJobs.find(
                    jobData => {
                      return jobData.outputs.find(output => output.relatedTo.id === scene.id && output.type == 'hls' && output.label != 'hlsTrimmed') !== undefined
                    }
                  );
                  if (sceneHlsJob) {
                    switch (jobLabel) {
                      case ENHANCE_JOB_LABEL:
                        enhancing = true;
                        break;
                      default:
                        processing = true;
                    }
                    jobsToCheck.push(sceneHlsJob.jobId);
                  }
                }
              }
            });
            const sceneJob = result.runningJobs.find(
              jobData => jobData.outputs.find(output => output.relatedTo.id === scene.id && (output.type == 'normalized' || output.type == 'hls') && output.label !== 'hlsTrimmed' && output.label != 'enhanced' && output.label != 'avatar') !== undefined
            );
            if (sceneJob) {
              processing = true;
              jobsToCheck.push(sceneJob.jobId);
            }
          } else {
            // If scene has resource array, but there is no hls resource yet, the job will start soon
            scene?.resources?.length > 0 && !stream && pushScenes([{ videoId, sceneId: scene?.originalId ?? scene?.id }]);
          }
          pushJobs(jobsToCheck);
          if (scene.video && !processing) {
            scene.video = stream?.url ?? '';
          }

          return {
            ...scene,
            storyId: video.id,
            author,
            isCompleted,
            thumbnail: thumbnail !== undefined ? thumbnail.url : null,
            waveform: waveform !== undefined ? waveform.url : null,
            processing,
            enhancing,
          };
        });
      setVideoByParts(prevState => ({ ...prevState, scenes: structuredClone(generatedScenes) }));
      setInit(false);
    };
    if (video) {
      initVideo();
    }
  }, [video]);

  const listenerFunc = () => {
    if (document.visibilityState == 'visible') {
      console.log('tab is active');
      setTimeout(async () => {
        const recordedStoryId = window.localStorage.getItem('recordedStoryId');
        if (recordedStoryId) {
          try {
            const story = await VideoService.getVideoDetails(recordedStoryId, ScenesType.REGULAR);
            setRecordedClip({ ...story.scenes[0], author: story.author, storyId: story.id, source: 'recorded' });
          } catch (e) {
            console.log('Scene with this ID doesnt exist', recordedStoryId);
          }
          window.localStorage.removeItem('recordedStoryId');
        }

        const updateClipScriptData = window.localStorage.getItem('updateClipScript');
        if (updateClipScriptData) {
          const parsedData = JSON.parse(updateClipScriptData);
          if (parsedData.storyId === videoId) {
            setVideoByParts(prevState => ({
              ...prevState,
              scenes: prevState.scenes.map(scene =>
                scene.id === parsedData.sceneId ? { ...scene, script: parsedData.script } : scene
              ),
            }));
          }
          window.localStorage.removeItem('updateClipScript');
        }

        const recordingIsCanceled = window.localStorage.getItem('userCancelDraftScene');
        if (recordingIsCanceled) {
          localStorage.removeItem('userCancelDraftScene');
          setNewlyRecordingSceneId(async sceneId => {
            if (sceneId) {
              // Remove unrecorded scene from the list
              await VideoService.deleteScene(sceneId);
              setVideoByParts(prevState => ({ ...prevState, scenes: prevState.scenes.filter(item => item.id !== sceneId) }));
            }
            return null;
          });
        }
      }, 250);
    }
  };

  const extensionMessageListener = async event => {
    // We only accept messages from this window to itself [i.e. not from any iframes]
    if (event.source !== window) return;
    event.data.type === `${EXTENSION_ID}-extension` &&
      console.log('editor: event.data.type', event.data.type, event.data.message, event);
    if (event.data.type && event.data.type === `${EXTENSION_ID}-extension`) {
      if (event.data.message === 'USER_MADE_A_CLIP') {
        console.log('Extension message received');
        if (event.data.storyId) {
          const story = await VideoService.getVideoDetails(event.data.storyId, ScenesType.REGULAR);
          setRecordedClip({ ...story.scenes[0], author: story.author, storyId: story.id, source: 'recorded' });
        }
      }
      if (event.data.message === 'SAVE_UPDATED_SCRIPT') {
        const { sceneId, storyId, script } = event.data;
        console.log('SAVE_UPDATED_SCRIPT', event.data);
        if (event.data.storyId === videoId) {
          await CmsAPI.updateScene(sceneId, { id: sceneId, storyId: storyId, script: script });
          setVideoByParts(prevState => ({
            ...prevState,
            scenes: prevState.scenes.map(scene => (scene.id === sceneId ? { ...scene, script: script } : scene)),
          }));
        }
      }
      if (event.data.message === 'USER_CANCEL_RECORDING') {
        console.log('USER_CANCEL_RECORDING');

        setNewlyRecordingSceneId(async sceneId => {
          console.log('newlyRecordingSceneId', sceneId);
          if (sceneId) {
            // Remove unrecorded scene from the list
            await VideoService.deleteScene(sceneId);
            setVideoByParts(prevState => ({ ...prevState, scenes: prevState.scenes.filter(item => item.id !== sceneId) }));
          }
          return null;
        });
        // window.localStorage.removeItem('recordedStoryId');
      }
    }
  };

  useEffect(() => {
    window.addEventListener('visibilitychange', listenerFunc);
    // To support old version of extension without recording studio
    window.addEventListener('message', extensionMessageListener);
    return () => {
      window.removeEventListener('visibilitychange', listenerFunc);
      // To support old version of extension without recording studio
      window.removeEventListener('message', extensionMessageListener);
      resetContext();
      resetVideoSpeedUpContext();
      //ResourceHelper.stopPolling();
    };
  }, []);

  useEffect(() => {
    if (recordedClip) {
      handleSaveSelectedClips(recordedClip);
      setRecordedClip(null);
    }
  }, [recordedClip]);

  useEffect(() => {
    const blockNavigation =
      hasChanged || (isZoomOpened && zoomChanged) || (trimmingOn && trimmingActionsBuffer.length > 0) || showUploadModal.show;
    let unblock = blockNavigation
      ? history.block(tx => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        if (confirm(t('clip_organizer.confirm_leave_page'))) {
          // Unblock the navigation.
          if (showUploadModal.show && !isClipUploading) {
            // Clip was not uploaded, remove the hidden clip from the story
            VideoService.deleteScene(showUploadModal.sceneData.sceneId);
          }
          return true;
        } else {
          return false;
        }
      })
      : history.block(() => {
        return true;
      });

    return () => unblock();
  }, [hasChanged, zoomChanged, isZoomOpened, trimmingOn, trimmingActionsBuffer]);

  useEffect(() => {
    setSceneIndex(initialVideoDetails.introMeta ? 1 : 0);
  }, []);

  useEffect(() => {
    if (sceneIndex >= scenes?.length) {
      setSceneIndex(initialVideoDetails.introMeta ? 1 : 0);
      return;
    }

    // If the user has added a new clip, then select added clip
    // (if the user has added several clips, then select the last added one)
    if (previousScenes.length && scenes.length > previousScenes.length) {
      const previousScenesIds = previousScenes.map(({ id }) => id);
      const scenesIds = scenes.map(({ id }) => id);

      const newSceneId = scenesIds?.findLast(sceneId => !previousScenesIds?.includes(sceneId));
      // Ignore outro selection if it was added
      if (newSceneId === 'Outro') {
        return;
      }
      const newScene = scenes.find(item => item.id === newSceneId);
      // If scene is hidden - do not select it yet
      if (newScene.hidden) {
        return;
      }

      const newSceneIndex = scenesIds.indexOf(newSceneId);
      handleSelectClip(getCurrentVideoPart(newSceneIndex));
    }
  }, [scenes]);

  useEffect(() => {
    const { intro, outro, scenes: scenesFromParts } = videoByParts;
    console.log('video by parts have been changed intro, outro, scenes', intro, outro, scenesFromParts);
    if (scenesFromParts?.length) {
      const isHasIntro = scenes.some(({ id }) => id === 'Intro');

      // If the intro was deleted, it is required to calculate a new index for the current scene,
      // since the scene has moved to the beginning of the array by 1
      if (isHasIntro && !intro) {
        setSceneIndex(getSceneIndex(currentVideoPart));
      }

      setScenes([intro, ...scenesFromParts, outro].filter(Boolean));
    }
  }, [videoByParts]);

  useEffect(() => {
    setCurrentVideoPart(getCurrentVideoPart(sceneIndex));
  }, [sceneIndex]);

  useEffect(() => {
    if (isStopBackgroundMusic) {
      setIsStopBackgroundMusic(false);
    }
  }, [isStopBackgroundMusic]);

  return (
    <TextPanelProvider>
      <ProtectedPageSidebar />
      <EditorContent
        storyId={videoId}
        scenes={scenes}
        transitions={transitions}
        sceneIndex={sceneIndex}
        videoByParts={videoByParts}
        currentVideoPart={currentVideoPart}
        video={video}
        logo={logo}
        handleEnded={handleEnded}
        handleSliderChange={handleSliderChange}
        handleSelectClip={handleSelectClip}
        onAddClip={onAddClip}
        onUploadClip={onUploadClipSelected}
        onAddEmptyClip={handleAddEmptyClip}
        handleScenesMove={handleScenesMove}
        handleDeleteScene={handleDeleteScene}
        handleDuplicateScene={handleDuplicateScene}
        handleSceneRename={handleSceneRename}
        handleTrimScene={handleTrimScene}
        controlsDisabled={controlsDisabled}
        undoLastAction={undoLastAction}
        redoLastAction={redoLastAction}
        handleIntroOutro={handleIntroOutro}
        handleSaveChanges={saveEdits}
        handleSaveTextPanel={handleSaveTextPanel}
        saving={saving}
        init={init}
        hasChanged={hasChanged || currentActionIndex > 0}
        onAddTransition={onAddTransition}
        onDeleteTransition={onDeleteTransition}
        onTransitionChange={onTransitionChange}
        onBackgroundMusicChange={onBackgroundMusicChange}
        handleSpeedUpChange={handleSpeedUpChange}
        updateSceneData={updateSceneData}
        isStopBackgroundMusic={isStopBackgroundMusic}
        handleSplit={handleSplitScene}
        handleUpdateScene={handleUpdateScene}
        handleRecordScene={handleRecordScene}
        userPermissions={userPermissions}
        handleCompleteAssignment={handleCompleteAssignment}
        handleCompleteAssignments={handleCompleteAssignments}
        handleLogoChange={handleLogoChange}
        handleZoomPointsChange={handleZoomPointsChange}
        assignments={assignments}
        handleReviseAssignment={handleReviseAssignment}
        handleCancelRecording={handleCancelRecording}
        onEnhanceScene={onEnhanceScene}
        onRemoveEnhanceScene={onRemoveEnhanceScene}
        handleGenerateAvatarClip={handleGenerateAvatarClip}
        videoConfig={videoConfig}
      />
      <ClipsProvider>
        <AddClipModal open={showSelectFromLibraryModal} onClose={handleCloseAllClipModal}>
          <AddClipContent onClose={handleCloseAllClipModal} onSave={handleSaveSelectedClips} />
        </AddClipModal>
      </ClipsProvider>
      <UploadClipModal open={showUploadModal?.show} onClose={handleCloseUploadModal}>
        <UploadClipTab
          sceneToUploadInfo={showUploadModal?.sceneData}
          onUploadDone={handleUploadClip}
          toggleClipUploading={setIsClipUploading}
        />
      </UploadClipModal>
      <InstallExtensionModal open={isShowExtensionModal} onClose={() => setIsShowExtensionModal(false)} />
    </TextPanelProvider>
  );
};

export default withPermissionCheck(Editor);
